/**
 * Shared OrganiserDropdown — searchable picker of organiser/admin/super_admin
 * candidates. Mirrors the mobile OrganiserDropdown.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useApi } from "@/lib/auth";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Check, ChevronDown, X } from "lucide-react";

function displayName(u) {
  const t = (u.masonic_title || "").trim();
  const base = u.name || `${u.first_name || ""} ${u.family_name || ""}`.trim();
  return `${t ? t + " " : ""}${base}`.trim();
}

export default function OrganiserDropdown({
  label,
  value,           // display string
  selectedUserId,  // canonical user_id
  onSelect,        // (candidate | null) => void
  placeholder = "Choose from organisers / admins",
  testId,
  required = false,
  hint,
}) {
  const api = useApi();
  const [candidates, setCandidates] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const rootRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api("/admin/organiser-candidates");
        if (r.ok) { const d = await r.json(); setCandidates(d.candidates || []); }
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return candidates;
    return candidates.filter((u) =>
      [displayName(u), u.email || "", u.role || ""].some((s) => s.toLowerCase().includes(ql))
    );
  }, [q, candidates]);

  const buttonLabel = value || placeholder;

  return (
    <div ref={rootRef} className="space-y-1">
      {label && (
        <Label>
          {label}{required && <span className="text-destructive"> *</span>}
        </Label>
      )}
      <div className="relative">
        <button
          type="button"
          data-testid={testId}
          onClick={() => setOpen((o) => !o)}
          className={[
            "w-full flex items-center justify-between gap-2 px-3 h-10 rounded-md border bg-white text-left text-sm",
            value ? "text-foreground border-border" : "text-muted-foreground border-input",
            "hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-ring",
          ].join(" ")}
        >
          <span className="truncate">{buttonLabel}</span>
          <div className="flex items-center gap-1 shrink-0">
            {value && (
              <span
                role="button"
                aria-label="Clear"
                onClick={(e) => { e.stopPropagation(); onSelect(null); }}
                className="p-1 rounded hover:bg-muted"
                data-testid={testId ? `${testId}-clear` : undefined}
              >
                <X size={14} />
              </span>
            )}
            <ChevronDown size={16} />
          </div>
        </button>
        {open && (
          <div className="absolute z-30 mt-1 w-full rounded-md border border-border bg-white shadow-lg" data-testid={testId ? `${testId}-menu` : undefined}>
            <div className="p-2 border-b border-border">
              <Input
                autoFocus
                placeholder="Search by name, email or role…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid={testId ? `${testId}-search` : undefined}
              />
            </div>
            <div className="max-h-64 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <div className="px-3 py-3 text-sm text-muted-foreground">No matches.</div>
              ) : filtered.map((u) => (
                <button
                  type="button"
                  key={u.user_id}
                  onClick={() => { onSelect(u); setOpen(false); setQ(""); }}
                  className={[
                    "w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50",
                    selectedUserId === u.user_id ? "bg-primary/5" : "",
                  ].join(" ")}
                  data-testid={testId ? `${testId}-opt-${u.user_id}` : undefined}
                >
                  <div className="flex-1">
                    <div className="font-medium">{displayName(u)}</div>
                    <div className="text-xs text-muted-foreground">{u.email} · {u.role}</div>
                  </div>
                  {selectedUserId === u.user_id && <Check size={14} className="text-primary" />}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

export { displayName };
